import React, {useCallback, useEffect, useMemo, useState} from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { useAppDispatch } from "../../store";
import { fetchSections } from "../../store/slices/sections";
import { fetchTestArchiveAuthors } from "../../store/slices/abTests";
import DatePicker from "react-datepicker";
import { ExportIcon } from "../../icons/Icons";
import Button from "../ui/common/Button";
import "react-datepicker/dist/react-datepicker.css";
import "./AbTestsFilters.scss"

type Option = {
  value: string;
  label: string;
};

type BrandState = {
  currentBrand: {
    isLoading: boolean;
    error: Object | null;
    data: {
      features: Array<string>;
      domain: string;
    } | null;
  };
};


type AbTestsFiltersProps = {
    updateFiltersInURL: Function
    loadingFilterParamsFromUrl : Boolean
    setLoadingFilterParamsFromUrl: Function
    setPageParam: Function,
    getCSV: Function
};


const paidStatusOptions = [
    {
        value: "all",
        label: "All"
    },
    {
        value: "paid",
        label: "Paid"
    },
    {
        value: "free",
        label: "Free"
    }
]

const typeStatusOptions = [
    {
        value: "all",
        label: "All"
    },
    {
        value: "headline",
        label: "Headline"
    },
    {
        value: "image",
        label: "Image"
    }
]

const AbTestsFilters = ({
    updateFiltersInURL,
    loadingFilterParamsFromUrl,
    setLoadingFilterParamsFromUrl,
    setPageParam,
    getCSV
}: AbTestsFiltersProps) => {
    const dispatch = useAppDispatch();
    const sections = useSelector((state: any) => state.sections.data);
    const authors = useSelector((state: any) => state.abTests.testAuthors); 
    
    const isLoadingSections = useSelector((state: any) => state.sections.isLoading);
    const isLoadingAbTests = useSelector((state: any) => state.abTests.isLoading);
    const isLoadingAbTestsAuthors = useSelector((state: any) => state.abTests.isLoadingAbTestsAuthors);

    // const [isLoadingUrlParams, setIsLoadingUrlParams] = useState(true);

    const [sectionSelectValue, setSectionSelectValue]: [{ label: string; value: string }[], Function] = useState([]);
    const [authorsSelectValue, setAuthorsSelectValue] : [{ label: string; value: string }[], Function] = useState([]);
    const brandInfo = useSelector((state: BrandState) => state.currentBrand.data);

    const [paidValue, setPaidValue] : [{label: string, value: string}, Function]  = useState(paidStatusOptions[0]);
    const [typeValue, setTypeValue] : [{label: string, value: string}, Function] = useState(typeStatusOptions[0])
    const [datePeriod, setDatePeriod] = useState({startDate: null, endDate: null}) as any;
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [datePickerUnselected, setDatePickerUnselected] = useState("")

     useEffect(() => {
       if (brandInfo?.domain) {
         dispatch(fetchSections("feed", brandInfo.domain));
         dispatch(fetchTestArchiveAuthors());
       }
     }, [brandInfo, dispatch]);

    const handleSectionsChange = (value: Option[]) => {
        setSectionSelectValue(value);
    }

    const handleAuthorsChange = (value : Option[]) => {
        setAuthorsSelectValue(value)
    }

    const getSectionOptions = useMemo(() => {
        return sections.map((section) => ({
        label: section.id.slice(1).replace(/\//g, " > "),
        value: section.id,
        }));
    }, [sections]);

    useEffect(()=>{
         if (isLoadingSections || loadingFilterParamsFromUrl || !datePeriod.endDate) return;
            updateFiltersInURL(datePeriod, sectionSelectValue, paidValue?.value, typeValue?.value, authorsSelectValue)
    }, [datePeriod, sectionSelectValue, paidValue?.value, typeValue?.value, authorsSelectValue, loadingFilterParamsFromUrl, isLoadingSections, updateFiltersInURL])


    const authorsList = useMemo(()=>{
        try {
            if (authors === undefined || !authors || authors.length === 0) return []
            return authors.map((author) => {
                return {
                    value: author.id,
                    label: author.name
                }
            })
        } catch {
            return []
        }
    },[authors])


    const applyFiltersFromUrl = useCallback(
         () => {
      
        const urlParams = new URLSearchParams(window.location.search);

        const authorsParam = urlParams.get("authors");
        const sectionsParam = urlParams.get("sections");
        const paidStatusParam = urlParams.get("paidStatus");
        const typeStatusParam = urlParams.get("type");
        const dateFromParam = urlParams.get("date_from");
        const dateToParam = urlParams.get("date_to");

        let sectionsValue = [] as any;

        if (authorsParam) {
            const authorsArray = authorsParam.split(",");
            const authorsObjects = authorsArray.map((author) => {
                return authors.find(e => {
                    return e.id === author
                })
            })
            const auhtorsValue = authorsObjects.map((author) => {
                return {
                    label: author.name,
                    value: author.id
                }
            })
            setAuthorsSelectValue(auhtorsValue);
        }

         if (sectionsParam) {
            const sectionsArray = sectionsParam.split(",");
            sectionsValue = sectionsArray.map((section) => {
                return getSectionOptions.find(e => {
                        if (e && e !== undefined) {
                            return e.value.includes(section);
                        } else return {}
                    })
            })

            setSectionSelectValue(sectionsValue);
        }

        if (paidStatusParam) {
            try {
                const selectedPaidStatus = paidStatusOptions.find(e => e.value === paidStatusParam)
                setPaidValue(selectedPaidStatus)
            } catch {
                setPaidValue(paidStatusOptions[0])
            }
        } else {
             setPaidValue(paidStatusOptions[0])
        }

         if (typeStatusParam) {
            try {
                const selectedTypeStatus = typeStatusOptions.find(e => e.value === typeStatusParam)
                setTypeValue(selectedTypeStatus)
            } catch {
                setTypeValue(typeStatusOptions[0])
            }
        } else {
            setTypeValue(typeStatusOptions[0])
        }

        if (dateFromParam && dateToParam) {
            setDatePeriod({startDate: new Date(dateFromParam), endDate: new Date(dateToParam)})
        } else {
            const days = 7
            const date = new Date();
            const start = new Date(date.getTime() - (days * 24 * 60 * 60 * 1000));
            setDatePeriod({startDate: start, endDate: date})
        }

        setLoadingFilterParamsFromUrl(false);
           

    },[authors, getSectionOptions, setLoadingFilterParamsFromUrl])
    
    useEffect(()=>{
        if (isLoadingSections) return;
        if (!loadingFilterParamsFromUrl) return;
         else {
            applyFiltersFromUrl();
          
        }
    },[isLoadingSections, sections, authors, isLoadingAbTests, loadingFilterParamsFromUrl, applyFiltersFromUrl])
 

    return (
        <div className="ab-tests-filters">

            <div className={`ab-test-filters-date-period ${datePickerUnselected}`}>
                <div className="ab-tests-filters-label">Date Range:</div>
                <DatePicker
                    selectsRange
                    startDate={datePeriod.startDate}
                    endDate={datePeriod.endDate}
                    open={isDatePickerOpen}
                    maxDate={new Date()}
                    onInputClick={()=>{
                        setIsDatePickerOpen(true);
                    }}
                    onChange={([start, end]) => {

                        setDatePeriod({
                            startDate: start,
                            endDate: end,
                        })
                        setPageParam(1)

                        if (start && end) {
                            setIsDatePickerOpen(false);
                            setDatePickerUnselected("")
                        }
                    }}
                    onClickOutside={()=>{
                        if (datePeriod.startDate && !datePeriod.endDate) {
                            // alert("Please select date range end date in order to continue")
                            setDatePickerUnselected("message unselected")
                            setTimeout(()=>{
                                setDatePickerUnselected("message")
                            },600)
                            return;
                        }
                        setIsDatePickerOpen(false);
                    }}
                    dateFormat="d MMM yyyy"
                    placeholderText="Select date range"
                />
            </div>


            <div className="ab-tests-filters-sections">

                <div className="ab-tests-filters-label">Sections:</div>
                <div className="ab-tests-filters-sections-select-container">

                    <Select
                        isMulti
                        name="sections"
                        options={getSectionOptions}
                        value={sectionSelectValue}
                        className="basic-single"
                        classNamePrefix="so-select"
                        isLoading={isLoadingSections}
                        onChange={(option, action) => {
                            if (action.action === "clear") {
                                setSectionSelectValue([])
                            }
                            handleSectionsChange(option as Option[])
                            setPageParam(1);
                        }}
                        
                    />
                </div>
            </div>

            <div className="ab-tests-free-paid-selector">
                <div className="ab-tests-filters-label">Free/Paid:</div>
                <div>
                    <Select
                        name="paidStatus"
                        options={paidStatusOptions}
                        value={paidValue}
                        defaultValue={paidValue}
                         className="paid-status-select"
                        classNamePrefix="so-select"
                        onChange={(option) => {
                            setPaidValue(option as Option)
                            setPageParam(1);
                        }}

                    />
                </div>
            </div>

              <div className="ab-tests-type-selector">
                <div className="ab-tests-filters-label">Type:</div>
                <div>
                     <Select
                        name="type"
                        options={typeStatusOptions}
                        value={typeValue}
                        defaultValue={typeValue}
                        className="type-status-select"
                        classNamePrefix="so-select"
                        onChange={(option) => {
                            setTypeValue(option as Option)
                            setPageParam(1)
                        }}

                    />

                   
                </div>
            </div>

            <div className="ab-tests-filters-authors">

                <div className="ab-tests-filters-label">Authors:</div>
                <div className="ab-tests-filters-autors-select-container">

                    <Select
                        isMulti
                        name="authors"
                        options={authorsList}
                        value={authorsSelectValue}
                        className="basic-single"
                        classNamePrefix="so-select"
                        isLoading={isLoadingAbTestsAuthors}
                        onChange={(option, action) => {
                               if (action.action === "clear") {
                                setAuthorsSelectValue([])
                            }
                            handleAuthorsChange(option as Option[])
                            setPageParam(1)
                        }}
                    />
                </div>
            </div>

            <div className="ab-tests-export-button-container">
                <Button variant={"default"} className="ab-tests-export-button" onClick={()=>{getCSV()}}>
                    <span>Export CSV</span>
                    <ExportIcon/>
                </Button>
            </div>

        </div>
    );
    };

export default AbTestsFilters;